import { adminActionButtonSx } from 'src/theme/adminActionButton';
import AdminModal from 'src/components/AdminModal';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  TextField,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography
} from '@mui/material';

import {
  VariableFieldPalette,
  VariableTemplateEditor,
  VariableTemplateEditorHandle
} from 'src/components/emailTemplates/VariableTemplateEditor';
import { CompanyEmailTemplateData } from 'src/contracts/companyProfile';
import {
  getReusableCompanyEmailTemplates,
  previewReusableCompanyEmailTemplate
} from 'src/helper/company_email_templates';

import { normalizeEmailList } from './quoteSafety';

const QUOTE_VARIABLE_FIELDS = [
  'quote_number',
  'job_number',
  'venue',
  'client_name',
  'event_name',
  'event_dates',
  'quote_link',
  'sender_name'
];

type QuoteEmailComposeModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: (body: string, subject: string, bccEmails?: string[], ccEmails?: string[], allowPlatformSender?: boolean, toEmails?: string[]) => void | boolean | Promise<unknown>;
  emailType: 'QUOTE' | 'DEPOSIT' | 'ACTUALS' | 'INVOICE';
  recipientEditor?: React.ReactNode;
  recipientsEditable?: boolean;
  recipientHint?: string;
  ccSupported?: boolean;
  bccSupported?: boolean;
  previewAmount?: number;
  pendingActuals?: Record<string, unknown>;
  initialBody?: string;
  eventName?: string;
  venueName?: string;
  address?: string;
  companyId?: number;
  quoteNumber?: string | number;
  clientName?: string;
  eventStartDate?: string;
  eventEndDate?: string;
  toEmails?: unknown;
  ccEmails?: unknown;
  firstSend?: boolean;
};

const previewErrorMessage = (error: unknown) => {
  const responseMessage = (
    error as { response?: { data?: { message?: unknown } } }
  )?.response?.data?.message;
  return typeof responseMessage === 'string' && responseMessage.trim()
    ? responseMessage
    : 'Unable to render the live email preview.';
};

export const QuoteEmailComposeModal = ({
  open,
  onClose,
  onSave,
  emailType,
  initialBody = '',
  eventName = '',
  venueName = '',
  address = '',
  companyId,
  quoteNumber = '',
  clientName = '',
  eventStartDate = '',
  eventEndDate = '',
  toEmails = [],
  ccEmails = [],
  firstSend = false,
  recipientEditor,
  recipientsEditable = false,
  recipientHint = 'Add at least one client email before sending.',
  ccSupported = true,
  bccSupported = false,
  previewAmount,
  pendingActuals
}: QuoteEmailComposeModalProps) => {
  const isQuote = emailType === 'QUOTE';
  const workflowLabel = {
    QUOTE: 'quote',
    DEPOSIT: 'deposit',
    ACTUALS: 'actuals',
    INVOICE: 'invoice'
  }[emailType];
  const title = isQuote
    ? firstSend
      ? 'Compose quote email'
      : 'Compose revised quote email'
    : `Compose ${workflowLabel} email`;
  const variableFields = isQuote
    ? QUOTE_VARIABLE_FIELDS
    : [
        ...(emailType === 'INVOICE' ? ['invoice_number'] : ['quote_number']),
        'job_number',
        'venue',
        'client_name',
        'event_name',
        'event_dates',
        ...(emailType === 'DEPOSIT'
          ? ['event_address', 'deposit_amount', 'payment_link']
          : emailType === 'INVOICE'
          ? ['invoice_total', 'invoice_link']
          : ['actuals_link']),
        'sender_name'
      ];
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [senderWarning, setSenderWarning] = useState<string | null>(null);
  useEffect(() => { if (!open) setSenderWarning(null); }, [open]);
  const defaultBody = useMemo(
    () =>
      !isQuote
        ? `Hello {{client_name}},\n\nYour ${workflowLabel} for {{event_name}} at {{venue}} is ready for review.`
        : firstSend
        ? 'Hello {{client_name}},\n\nYour quote for {{event_name}} at {{venue}} is ready for review.'
        : 'Hello {{client_name}},\n\nYour revised quote for {{event_name}} at {{venue}} is ready for review.',
    [firstSend, isQuote, workflowLabel]
  );
  const defaultSubject = isQuote
    ? 'Quote #{{quote_number}} - {{venue}}'
    : `${emailType.charAt(0)}${emailType
        .slice(1)
        .toLowerCase()} #{{job_number}} - {{venue}}`;
  const [emailTemplates, setEmailTemplates] = useState<
    CompanyEmailTemplateData[]
  >([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    null
  );
  const [emailTemplateBody, setEmailTemplateBody] = useState(
    initialBody || defaultBody
  );
  const [emailTemplateSubject, setEmailTemplateSubject] =
    useState(defaultSubject);
  const [activeEditor, setActiveEditor] = useState<'subject' | 'body'>('body');
  const bodyForSend = () => {
    if (!isQuote) return emailTemplateBody;
    let insideAnchor = false;
    return emailTemplateBody.split(/(<[^>]+>)/g).map(part => {
      if (part.startsWith('<')) { if (/^<a\b/i.test(part)) insideAnchor = true; if (/^<\/a\s*>/i.test(part)) insideAnchor = false; return part; }
      return insideAnchor ? part : part.replace(/{{\s*quote_link\s*}}/gi, '<a href="{{quote_link}}">View Quote</a>');
    }).join('');
  };
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const subjectEditorRef = useRef<VariableTemplateEditorHandle | null>(null);
  const bodyEditorRef = useRef<VariableTemplateEditorHandle | null>(null);
  const [editedToEmails, setEditedToEmails] = useState<string[]>([]);
  const [toInput, setToInput] = useState('');
  const normalizedToEmails = recipientsEditable && !recipientEditor
    ? normalizeEmailList([...editedToEmails, toInput]) : normalizeEmailList(toEmails);
  const toSeed = normalizeEmailList(toEmails).join(',');
  useEffect(() => { if (open) { setEditedToEmails(normalizeEmailList(toSeed)); setToInput(''); } }, [open, toSeed]);
  const [editedCcEmails, setEditedCcEmails] = useState<string[]>([]);
  const [ccInput, setCcInput] = useState('');
  const normalizedCcEmails = (recipientsEditable || bccSupported)
    ? normalizeEmailList([...editedCcEmails, ccInput]) : normalizeEmailList(ccEmails);
  const ccSeed = normalizeEmailList(ccEmails).join(',');
  useEffect(() => { if (open) { setEditedCcEmails(normalizeEmailList(ccSeed)); setCcInput(''); } }, [open, ccSeed]);
  const [bccEmails, setBccEmails] = useState<string[]>([]);
  const [bccInput, setBccInput] = useState('');
  const bccEnabled = recipientsEditable || (isQuote && bccSupported);
  const selectedBcc = normalizeEmailList([...bccEmails, bccInput]);
  const validEmail = (email: string) => email.length <= 254 && email.split('@')[0].length <= 64 && !email.startsWith('.') && !email.includes('..') && !email.includes('.@') && /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/.test(email);
  const invalidBcc = bccEnabled && selectedBcc.some(email => !validEmail(email));
  const invalidVisible = [...normalizedToEmails, ...(ccSupported ? normalizedCcEmails : [])].some(email => !validEmail(email));
  const recipientRoles = new Map<string, number>();
  const duplicateRoles = [normalizedToEmails, ccSupported ? normalizedCcEmails : [], bccEnabled ? selectedBcc : []].some((emails, role) =>
    emails.some(email => {
      const key = email.toLowerCase();
      if (recipientRoles.has(key) && recipientRoles.get(key) !== role) return true;
      recipientRoles.set(key, role); return false;
    }));
  const hasRecipient = normalizedToEmails.length > 0 && !invalidVisible && !invalidBcc && !duplicateRoles;
  useEffect(() => { if (!open) { setBccEmails([]); setBccInput(''); } }, [open]);

  useEffect(() => {
    if (!open) return;
    setSendError(null);
    setEmailTemplates([]);
    setSelectedTemplateId(null);
    setEmailTemplateBody(initialBody || defaultBody);
    setEmailTemplateSubject(defaultSubject);
    setActiveEditor('body');
  }, [defaultBody, defaultSubject, initialBody, open]);

  useEffect(() => {
    if (!open || !emailType || !companyId) return;
    let active = true;
    getReusableCompanyEmailTemplates(companyId, emailType)
      .then((templates) => {
        if (!active) return;
        setEmailTemplates(templates);
        const selected = templates[0];
        if (selected) {
          setSelectedTemplateId(selected.id);
          setEmailTemplateBody(initialBody || selected.body || defaultBody);
          setEmailTemplateSubject(selected.subject || defaultSubject);
        }
      })
      .catch((error) => {
        if (active) console.error('Failed to load email templates:', error);
      });
    return () => {
      active = false;
    };
  }, [companyId, defaultBody, defaultSubject, initialBody, emailType, open]);

  const resolvePreviewAmounts = (value: string) =>
    value.replace(/{{\s*(deposit_amount|invoice_total)\s*}}/gi, () =>
      Number.isFinite(previewAmount)
        ? String(previewAmount)
        : 'Amount unavailable'
    );

  useEffect(() => {
    if (!open || !companyId) {
      setPreviewHtml('');
      setPreviewSubject('');
      setPreviewError(null);
      setPreviewLoading(false);
      return;
    }
    let active = true;
    setPreviewLoading(true);
    setPreviewError(null);
    const timer = window.setTimeout(() => {
      previewReusableCompanyEmailTemplate(companyId, {
        variant: isQuote
          ? firstSend
            ? 'INITIAL_QUOTE'
            : 'REVISED_QUOTE'
          : emailType,
        pendingActuals: emailType === 'ACTUALS' ? pendingActuals : undefined,
        subject: resolvePreviewAmounts(emailTemplateSubject),
        body: resolvePreviewAmounts(emailTemplateBody),
        event: {
          id: quoteNumber,
          name: eventName,
          venue: venueName,
          address,
          ...(emailType === 'DEPOSIT' && Number.isFinite(previewAmount)
            ? { depositPaid: previewAmount }
            : {}),
          billTo: clientName,
          startDate: eventStartDate,
          endDate: eventEndDate
        }
      })
        .then((preview) => {
          if (!active) return;
          const previewDocument = new DOMParser().parseFromString(preview.html, 'text/html');
          if (isQuote) {
            const action = previewDocument.querySelector<HTMLAnchorElement>('a.communication-action');
            const quoteUrl = action?.getAttribute('href');
            if (quoteUrl) {
              const walker = previewDocument.createTreeWalker(previewDocument.body, NodeFilter.SHOW_TEXT);
              const nodes: Text[] = [];
              while (walker.nextNode()) nodes.push(walker.currentNode as Text);
              nodes.forEach(node => {
                if (node.parentElement?.closest('a, style, script') || !node.textContent?.includes(quoteUrl)) return;
                const parts = node.textContent.split(quoteUrl);
                const fragment = previewDocument.createDocumentFragment();
                parts.forEach((part, index) => {
                  if (index) { const link = previewDocument.createElement('a'); link.href = quoteUrl; link.textContent = 'View Quote'; fragment.append(link); }
                  fragment.append(previewDocument.createTextNode(part));
                });
                node.replaceWith(fragment);
              });
            }
          }
          setPreviewHtml(previewDocument.documentElement.outerHTML);
          setPreviewSubject(preview.subject);
        })
        .catch((error: unknown) => {
          if (active) setPreviewError(previewErrorMessage(error));
        })
        .finally(() => {
          if (active) setPreviewLoading(false);
        });
    }, 350);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [
    address,
    clientName,
    companyId,
    emailTemplateBody,
    emailTemplateSubject,
    eventEndDate,
    eventName,
    eventStartDate,
    firstSend,
    open,
    quoteNumber,
    venueName,
    emailType,
    isQuote,
    previewAmount,
    pendingActuals
  ]);

  const selectTemplate = (template: CompanyEmailTemplateData) => {
    setSelectedTemplateId(template.id);
    setEmailTemplateBody(template.body || defaultBody);
    setEmailTemplateSubject(template.subject || defaultSubject);
  };

  const close = () => {
    if (!sendingRef.current) onClose();
  };
  const handleSave = async (allowPlatformSender = false) => {
    if (!hasRecipient || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    setSendError(null);
    try {
      const seenPrivate = new Set<string>();
      const privateRecipients = selectedBcc.filter(email => {
        const key = email.toLowerCase();
        if (seenPrivate.has(key)) return false;
        seenPrivate.add(key); return true;
      });
      const result = recipientsEditable
        ? await onSave(bodyForSend(), emailTemplateSubject, privateRecipients, normalizedCcEmails, allowPlatformSender, normalizedToEmails)
        : bccEnabled
        ? allowPlatformSender
          ? await onSave(bodyForSend(), emailTemplateSubject, privateRecipients, normalizedCcEmails, true)
          : await onSave(bodyForSend(), emailTemplateSubject, privateRecipients, normalizedCcEmails)
        : await onSave(bodyForSend(), emailTemplateSubject);
      if (result === false || result === null)
        throw new Error(
          'Email was not sent. Please review the details and try again.'
        );
      onClose();
    } catch (error) {
      const message = (error as any)?.response?.data?.message || (error as Error)?.message || '';
      if (isQuote && message.includes('Continue With Platform Sender')) setSenderWarning(message);
      setSendError(
        (error as any)?.response?.data?.message ||
          (error as Error)?.message ||
          'Unable to send email. Please try again.'
      );
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  return (
    <AdminModal
      open={open}
      onClose={close}
      aria-labelledby="email-template-modal"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: {
            xs: 'calc(100% - 24px)',
            lg: 'min(1400px, calc(100% - 48px))'
          },
          height: {
            xs: 'calc(100% - 24px)',
            lg: 'min(900px, calc(100% - 48px))'
          },
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 0,
          '& > [data-admin-dialog-header], & > [data-admin-dialog-footer]': {
            py: 1
          },
          '& > [data-admin-dialog-body]': {
            py: 1,
            display: 'flex',
            flexDirection: 'column'
          },
          borderRadius: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0
        }}
      >
        <Typography id="email-template-modal" variant="h6" sx={{ mb: 0 }}>
          {title}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              lg: 'minmax(360px, 0.8fr) minmax(500px, 1.2fr)'
            },
            gap: 2,
            flex: '1 1 auto',
            minHeight: 0,
            overflow: { xs: 'auto', lg: 'hidden' }
          }}
        >
          <Stack
            spacing={2}
            sx={{ minWidth: 0, overflowY: { lg: 'auto' }, pr: { lg: 0.5 } }}
          >
            <Box sx={{ p: 1.5, bgcolor: '#f7f7f7', borderRadius: 1 }}>
              {recipientEditor || (recipientsEditable ? <Autocomplete multiple freeSolo options={[]}
                value={editedToEmails} inputValue={toInput}
                onInputChange={(_, value) => setToInput(value)}
                onChange={(_, values) => { setEditedToEmails(normalizeEmailList(values)); setToInput(''); }}
                disabled={sending}
                renderInput={params => <TextField {...params} margin="dense" label="To" placeholder="Add Recipient Email"
                  error={normalizedToEmails.some(email => !validEmail(email))} />} /> : (
                <Typography variant="body2">
                  <strong>To:</strong>{' '}
                  {normalizedToEmails.join(', ') || 'No recipient selected'}
                </Typography>
              ))}
              {bccEnabled && ccSupported ? <Autocomplete multiple freeSolo options={[]}
                value={editedCcEmails} inputValue={ccInput}
                onInputChange={(_, value) => setCcInput(value)}
                onChange={(_, values) => { setEditedCcEmails(normalizeEmailList(values)); setCcInput(''); }}
                disabled={sending}
                renderInput={params => <TextField {...params} margin="dense" label="CC (Optional)" placeholder="Add CC Email"
                  error={normalizedCcEmails.some(email => !validEmail(email))}
                  helperText={isQuote ? "Copied recipients open their own link and view the live quote read-only." : "Copied recipients are visible to other recipients."} />} />
                : <Typography variant="body2"><strong>CC:</strong>{' '}{ccSupported ? normalizedCcEmails.join(', ') || 'None' : 'Not sent by this workflow'}</Typography>}
              {bccEnabled && (
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={bccEmails}
                  inputValue={bccInput}
                  onInputChange={(_, value) => setBccInput(value)}
                  onChange={(_, values) => { setBccEmails(normalizeEmailList(values)); setBccInput(''); }}
                  disabled={sending}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label="BCC (Optional)"
                      placeholder="Add BCC Email"
                      error={invalidBcc}
                      helperText={invalidBcc ? 'Enter a valid BCC email address.' : isQuote ? 'BCC recipients receive their own read-only link and stay hidden from other recipients.' : 'BCC recipients stay hidden from other recipients.'}
                    />
                  )}
                />
              )}
              {duplicateRoles && <Typography role="alert" color="error" variant="body2">Resolve duplicate recipients across To, CC and BCC before sending.</Typography>}
              {invalidVisible && <Typography role="alert" color="error" variant="body2">Enter valid To and CC email addresses before sending.</Typography>}
              {normalizedToEmails.length === 0 && (
                <Typography
                  role="alert"
                  variant="body2"
                  color="error"
                  sx={{ mt: 1 }}
                >
                  {recipientHint}
                </Typography>
              )}
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant={selectedTemplateId === null ? 'contained' : 'outlined'}
                sx={{
                  borderColor: '#E8BF21',
                  color: '#000',
                  textTransform: 'none'
                }}
                onClick={() => {
                  setSelectedTemplateId(null);
                  setEmailTemplateBody(defaultBody);
                  setEmailTemplateSubject(defaultSubject);
                }}
              >
                Default
              </Button>
              {emailTemplates.map((template) => (
                <Button
                  key={template.id}
                  size="small"
                  variant={
                    selectedTemplateId === template.id
                      ? 'contained'
                      : 'outlined'
                  }
                  sx={{
                    borderColor: '#E8BF21',
                    color: '#000',
                    textTransform: 'none'
                  }}
                  onClick={() => selectTemplate(template)}
                >
                  {template.title}
                  {template.isDefault ? ' - Default' : ''}
                </Button>
              ))}
            </Stack>

            <Divider />
            <VariableTemplateEditor
              ref={subjectEditorRef}
              id="quote-email-subject"
              label="Subject"
              value={emailTemplateSubject}
              placeholder="Add a subject line"
              onFocus={() => setActiveEditor('subject')}
              onChange={setEmailTemplateSubject}
            />
            <VariableTemplateEditor
              ref={bodyEditorRef}
              id="quote-email-body"
              label="Message body"
              value={emailTemplateBody}
              multiline
              richHtml={!isQuote && /<[a-z][\s\S]*>/i.test(emailTemplateBody)}
              placeholder="Write the message that appears above the quote action."
              onFocus={() => setActiveEditor('body')}
              onChange={setEmailTemplateBody}
            />
            <Box>
              <Typography variant="subtitle2">Add variable fields</Typography>
              <Typography variant="caption" color="text.secondary">
                Drag or click to insert into the active field.
              </Typography>
              <Box sx={{ mt: 1 }}>
                <VariableFieldPalette
                  fields={variableFields}
                  onInsert={(field) => {
                    const target =
                      activeEditor === 'subject'
                        ? subjectEditorRef.current
                        : bodyEditorRef.current;
                    target?.insertVariable(field);
                  }}
                />
              </Box>
            </Box>
          </Stack>

          <Box
            sx={{
              minWidth: 0,
              minHeight: { xs: 520, lg: 0 },
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography variant="subtitle1" fontWeight={700}>
              Live Email Preview
            </Typography>
            {!isQuote && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Preview uses this event and company branding. Action links are
                examples; delivery creates the final links.
              </Typography>
            )}
            <Typography variant="body2" sx={{ mb: 1 }}>
              {previewSubject || 'Rendering subject...'}
            </Typography>
            <Box
              sx={{
                position: 'relative',
                flex: '1 1 auto',
                minHeight: 0,
                border: '1px solid #d8d2c7',
                borderRadius: 1.25,
                bgcolor: '#f0f2f5',
                overflow: 'hidden'
              }}
            >
              {previewLoading && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    zIndex: 1,
                    bgcolor: 'rgba(255,255,255,0.9)',
                    borderRadius: 1,
                    px: 1.25,
                    py: 0.75
                  }}
                >
                  <CircularProgress size={16} />
                  <Typography variant="caption">
                    Refreshing preview...
                  </Typography>
                </Stack>
              )}
              {previewError ? (
                <Alert severity="error" sx={{ m: 2 }}>
                  {previewError}
                </Alert>
              ) : previewHtml ? (
                <Box
                  component="iframe"
                  title={
                    isQuote
                      ? `${
                          firstSend ? 'Initial' : 'Revised'
                        } quote email preview`
                      : `${workflowLabel} email preview`
                  }
                  sandbox="allow-same-origin"
                  srcDoc={previewHtml}
                  sx={{
                    border: 0,
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    minHeight: { xs: 520, lg: 0 }
                  }}
                />
              ) : (
                <Typography color="text.secondary" sx={{ p: 2 }}>
                  Email preview will appear here.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {senderWarning && <Alert severity="warning" sx={{ mt: 1, border: '1px solid', borderColor: 'warning.main' }}>
          {senderWarning}
          <Button disabled={sending || !hasRecipient} onClick={() => void handleSave(true)}>Continue With Platform Sender</Button>
        </Alert>}
        {sendError && !sendError.includes('Continue With Platform Sender') && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {sendError}
          </Alert>
        )}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={2}
          sx={{ pt: 0 }}
        >
          <Button
            sx={{
              width: 100,
              borderColor: '#E8BF21',
              borderWidth: 2,
              color: '#000'
            }}
            variant="outlined"
            onClick={close}
            disabled={sending}
          >
            Cancel
          </Button>
          <Button
            sx={{
              width: 100,
              ...adminActionButtonSx
            }}
            variant="contained"
            onClick={() => void handleSave()}
            disabled={!hasRecipient || sending}
            title={
              normalizedToEmails.length === 0
                ? 'A client email recipient is required.'
                : duplicateRoles
                ? 'Resolve duplicate recipients across To, CC and BCC before sending.'
                : invalidBcc
                ? 'Enter a valid BCC email address.'
                : invalidVisible
                ? 'Enter valid To and CC email addresses.'
                : undefined
            }
          >
            {sending ? 'Sending...' : 'Send'}
          </Button>
        </Stack>
      </Box>
    </AdminModal>
  );
};

export type { QuoteEmailComposeModalProps };
